import React, { useState } from 'react';
import styles from './Sales.module.css';
import salesData from '../../data/salesData.json'; // Import sales data
import { Tooltip, IconButton, Pagination } from '@mui/material';

// Import Icons
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Define the structure of a sale
interface Sale {
    id: number;
    saleCode: string;
    customerCode: string;
    customerName: string;
    date: string;
    status: string;
    items: {
        name: string;
        quantity: number;
        unitPrice: number;
        total: number;
    }[];
    totalAmount: number;
    paymentStatus: string;
    paymentMethod: string;
    notes: string;
}

const Sales: React.FC = () => {
    const [sales, setSales] = useState<Sale[]>(salesData);
    // Filter states
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [customerFilter, setCustomerFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [fulfillmentStatusFilter, setFulfillmentStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sales.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sales.length / itemsPerPage);

    // Placeholder handlers
    const handleSearch = () => {
        console.log('Searching Sales...', { fromDate, toDate, customerFilter, paymentStatusFilter, fulfillmentStatusFilter, searchTerm });
        // Add actual filtering logic here
    };

    const handleReset = () => {
        setFromDate('');
        setToDate('');
        setCustomerFilter('');
        setPaymentStatusFilter('');
        setFulfillmentStatusFilter('');
        setSearchTerm('');
        console.log('Sales Filters reset');
        // Maybe refetch or reset to original data
    };

    const handleAddSale = () => {
        console.log('Add new sale/order');
        // Logic to open a modal or navigate to an add form
    };

    const handleViewDetails = (id: number) => {
        console.log(`View details for sale ID: ${id}`);
    };
    
    const handleEditSale = (id: number) => {
      console.log(`Edit sale ID: ${id}`);
    };

    const handleDeleteSale = (id: number) => {
        console.log('Delete sale:', id);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Function to get a CSS class based on status
    const getStatusClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'paid':
            case 'delivered':
            case 'shipped':
                return styles.success;
            case 'pending':
            case 'processing':
                return styles.warning;
            case 'overdue':
            case 'cancelled': // Add other potential statuses
                return styles.danger;
            default:
                return styles.default;
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className={styles.container}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h2>Sales Orders</h2>
                    <p>Create and manage sales orders</p>
                </div>
                <div className={styles.actionsSection}>
                    {/* Add relevant buttons like Export */}
                    <button className={styles.actionButton}><FileDownloadIcon fontSize="small"/> Export Sales</button>
                    <button className={`${styles.actionButton} ${styles.addButton}`} onClick={handleAddSale}><AddShoppingCartIcon fontSize="small"/> New Order</button>
                </div>
            </div>

            {/* Filter Area */}
            <div className={styles.filterArea}>
                 <div className={styles.filterGroup}>
                    <label>From Date</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                 <div className={styles.filterGroup}>
                    <label>To Date</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
                 <div className={styles.filterGroup}>
                    <label>Customer</label>
                    {/* Consider a searchable dropdown for many customers */}
                    <input type="text" placeholder="Customer Name" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}/>
                </div>
                 <div className={styles.filterGroup}>
                    <label>Payment Status</label>
                    <select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)}>
                        <option value="">All</option>
                        <option value="Paid">Paid</option>
                        <option value="Pending">Pending</option>
                        <option value="Overdue">Overdue</option>
                    </select>
                </div>
                 <div className={styles.filterGroup}>
                    <label>Fulfillment Status</label>
                    <select value={fulfillmentStatusFilter} onChange={(e) => setFulfillmentStatusFilter(e.target.value)}>
                        <option value="">All</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Processing">Processing</option>
                        <option value="Delivered">Delivered</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>Search</label>
                    <input 
                        type="text" 
                        placeholder="Order ID / Customer"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                 <div className={styles.filterActions}>
                    <button className={`${styles.actionButton} ${styles.searchButton}`} onClick={handleSearch}><SearchIcon fontSize="small"/> Search</button>
                    <button className={`${styles.actionButton} ${styles.resetButton}`} onClick={handleReset}>Reset</button>
                </div>
            </div>

            {/* Table Area */}
            <div className={styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Sale Code</th>
                            <th>Date</th>
                            <th>Customer Name</th>
                            <th>Total Amount</th>
                            <th>Payment Status</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((sale, index) => (
                            <tr key={sale.id}>
                                <td>{index + 1}</td>
                                <td className={styles.orderId}>{sale.saleCode}</td>
                                <td>{sale.date}</td>
                                <td>{sale.customerName}</td>
                                <td>${sale.totalAmount.toFixed(2)}</td>
                                <td><span className={getStatusClass(sale.paymentStatus)}>{sale.paymentStatus}</span></td>
                                <td><span className={getStatusClass(sale.status)}>{sale.status}</span></td>
                                <td className={styles.actions}>
                                    <Tooltip title="View Details">
                                        <VisibilityIcon fontSize="small" onClick={() => handleViewDetails(sale.id)} />
                                    </Tooltip>
                                    <Tooltip title="Edit Sale">
                                        <EditIcon fontSize="small" onClick={() => handleEditSale(sale.id)} />
                                    </Tooltip>
                                    <Tooltip title="Delete Sale">
                                        <DeleteIcon fontSize="small" onClick={() => handleDeleteSale(sale.id)} />
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={styles.paginationContainer}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </div>
        </div>
    );
};

export default Sales; 